import { createTheme, type Theme } from '@mui/material/styles'
import type { Direction } from '@mui/material/styles'

/**
 * Material Design 3 palette, keyed to the place rather than to a stock swatch.
 *
 * The primary is the colour of Puruvesi on an overcast day, the secondary is birch bark in
 * October, and the surface is the near-white of snow light. Every one of these was checked
 * against WCAG AA for its intended foreground: the site is read by people over fifty on
 * phones outdoors, which is the least forgiving contrast environment there is.
 */
const lake = {
  50: '#E6F3F5',
  100: '#C0E2E7',
  200: '#96CFD8',
  300: '#6BBCC8',
  400: '#4BADBC',
  500: '#2B9EB0',
  600: '#0B6E7A',
  700: '#095A64',
  800: '#07454D',
  900: '#043038',
} as const

const birch = {
  50: '#FBF6EE',
  100: '#F4E8D6',
  200: '#EAD5B4',
  300: '#DFC191',
  400: '#D2AC6D',
  500: '#B98E45',
  600: '#96702F',
  700: '#6F5222',
  800: '#4A3616',
  900: '#2A1E0C',
} as const

/** Season accents. Used for the activity tabs and nothing else, so they can be vivid. */
export const seasonColors = {
  WINTER: '#5B8DEF',
  SPRING: '#57B894',
  SUMMER: '#E5A13A',
  AUTUMN: '#C4622D',
  ALL_YEAR: lake[600],
} as const

/**
 * Hebrew gets its own font stack. Inter has no Hebrew glyphs, so without this the
 * Hebrew site would silently fall back to whatever the device happens to have.
 */
const bodyFont = (direction: Direction) =>
  direction === 'rtl'
    ? '"Noto Sans Hebrew", "Inter", system-ui, -apple-system, "Segoe UI", sans-serif'
    : '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'

const displayFont = (direction: Direction) =>
  direction === 'rtl'
    ? '"Noto Sans Hebrew", Georgia, serif'
    : '"Fraunces", Georgia, "Times New Roman", serif'

export function buildTheme(direction: Direction): Theme {
  return createTheme({
    direction,
    palette: {
      mode: 'light',
      primary: {
        main: lake[600],
        light: lake[400],
        dark: lake[800],
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: birch[600],
        light: birch[400],
        dark: birch[800],
        contrastText: '#FFFFFF',
      },
      success: { main: '#2E7D53' },
      warning: { main: '#B45309' },
      error: { main: '#B3261E' },
      info: { main: lake[500] },
      background: {
        default: '#F7FAFA',
        paper: '#FFFFFF',
      },
      text: {
        primary: '#101F22',
        secondary: '#4A5C5F',
      },
      divider: 'rgba(11, 110, 122, 0.14)',
    },

    shape: { borderRadius: 16 },

    typography: {
      fontFamily: bodyFont(direction),
      h1: { fontFamily: displayFont(direction), fontWeight: 600, fontSize: 'clamp(2.2rem, 5vw, 3.4rem)', lineHeight: 1.1, letterSpacing: '-0.02em' },
      h2: { fontFamily: displayFont(direction), fontWeight: 600, fontSize: 'clamp(1.7rem, 3.6vw, 2.4rem)', lineHeight: 1.18, letterSpacing: '-0.015em' },
      h3: { fontFamily: displayFont(direction), fontWeight: 600, fontSize: 'clamp(1.35rem, 2.6vw, 1.75rem)', lineHeight: 1.25 },
      h4: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.3 },
      h5: { fontWeight: 600, fontSize: '1.05rem' },
      h6: { fontWeight: 600, fontSize: '0.95rem' },
      subtitle1: { fontSize: '1.05rem', lineHeight: 1.6, color: '#4A5C5F' },
      body1: { fontSize: '1rem', lineHeight: 1.7 },
      body2: { fontSize: '0.9rem', lineHeight: 1.6 },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
      overline: { textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 600, fontSize: '0.72rem' },
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ':root': {
            colorScheme: 'light',
          },
          body: {
            WebkitFontSmoothing: 'antialiased',
            textRendering: 'optimizeLegibility',
          },
          // Respect the visitor who has asked their OS to stop animating things.
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
          'img, video': { maxWidth: '100%', height: 'auto' },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 999, paddingInline: 22, minHeight: 44 },
          sizeLarge: { minHeight: 52, paddingInline: 30, fontSize: '1rem' },
        },
      },

      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: '1px solid rgba(11, 110, 122, 0.12)',
            transition: 'box-shadow 180ms ease, transform 180ms ease',
            overflow: 'hidden',
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500, borderRadius: 10 },
          // Amenity and activity chips lead with an emoji, so give it room to breathe.
          label: { paddingInline: 10 },
        },
      },

      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          root: {
            backdropFilter: 'saturate(180%) blur(12px)',
            backgroundColor: 'rgba(255, 255, 255, 0.86)',
            borderBottom: '1px solid rgba(11, 110, 122, 0.12)',
          },
        },
      },

      MuiTextField: { defaultProps: { variant: 'outlined', size: 'medium' } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 14 } } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 48 } } },
      MuiTooltip: { defaultProps: { arrow: true } },
      MuiLink: { defaultProps: { underline: 'hover' } },

      MuiContainer: { defaultProps: { maxWidth: 'lg' } },
    },
  })
}

export const palette = { lake, birch }
